<template>
  <b-card body-class="pt-1">
    <slot name="hint" />
    <b-card-title class="d-flex justify-content-between align-items-center mb-0">
      <span>Product Returns</span>
      <b-form-group class="mb-0">
        <b-form-radio-group
          v-model="selectedMode"
          :options="modeOptions"
          name="matured-products-mode-selection"
          button-variant="outline-primary"
          buttons
        />
      </b-form-group>
    </b-card-title>
    <div class="font-small-3 mb-1 text-muted" style="margin-top: -0.7rem;">
      Annualised total returns for all products
    </div>
    <b-card-text>
      <div v-if="loading">
        <loading-container />
      </div>
      <div
        v-else
        class="position-relative"
      >
        <div
          v-if="noData"
          class="no-data-overlay"
        >
          <span class="text-muted">No data available</span>
        </div>
        <app-echart-bar
          :key="chartKey"
          :option-data="chartData"
        />
      </div>
    </b-card-text>
  </b-card>
</template>

<script>
import {
  BCard, BCardTitle, BCardText, BFormGroup, BFormRadioGroup,
} from 'bootstrap-vue'
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import renderChartToBase64 from '../../breakdown/charts/offscreenRender'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardText,
    BFormGroup,
    BFormRadioGroup,
    AppEchartBar,
    LoadingContainer,
  },
  props: {
    returnsData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      noData: false,
      selectedMode: 'all',
      modeOptions: [
        { text: 'All', value: 'all' },
        { text: 'Live', value: 'live' },
        { text: 'Matured', value: 'matured' },
      ],
      chartKey: 0,
      chartData: {
        legend: {
          show: false,
        },
        grid: {
          left: 70,
          right: 30,
          bottom: 70,
          top: 20,
          containLabel: false,
        },
        xAxis: {
          type: 'category',
          data: [],
          name: 'Return (p.a.)',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Frequency',
          nameLocation: 'middle',
          nameGap: 45,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series: [],
      },
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
  },
  watch: {
    returnsData: {
      handler(data) {
        this.updateChart(data)
      },
      immediate: true,
    },
    selectedMode() {
      this.updateChart(this.returnsData)
    },
    skin() {
      // Re-process data when theme changes
      this.updateChart(this.returnsData)
    },
  },
  methods: {
    getDataURL() {
      if (this.loading) return null
      this.updateChart(this.returnsData)
      return this.noData ? null : renderChartToBase64(this.chartData)
    },
    updateChart(data) {
      if (!data || !Array.isArray(data.returns) || data.returns.length === 0) {
        this.setNoData()
        return
      }

      try {
        this.processReturnsData(data)
      } catch (err) {
        console.error('Error processing product returns chart:', err)
        this.setNoData()
      }
    },
    setNoData() {
      this.noData = true
      this.chartData = {
        ...this.chartData,
        xAxis: {
          ...this.chartData.xAxis,
          data: [],
        },
        series: [],
      }
      this.chartKey += 1
    },
    processReturnsData(data) {
      // Find the data for the selected mode
      const returnsData = data.returns.find(
        item => item.name.toLowerCase() === this.selectedMode,
      )

      if (!returnsData) {
        this.setNoData()
        return
      }

      let categories = []
      let series = []

      if (this.selectedMode === 'all') {
        if (!Array.isArray(returnsData.series)) {
          this.setNoData()
          return
        }

        // "All" mode uses series array with xaxis.categories
        categories = returnsData.xaxis?.categories || []
        const colors = ['#28c76f', '#1a73e8']
        series = returnsData.series.map((s, index) => ({
          name: s.name,
          type: 'bar',
          stack: 'total',
          data: Array.isArray(s.data) ? s.data : [],
          itemStyle: {
            color: colors[index % colors.length],
          },
        }))
        this.noData = !series.some(s => s.data.some(v => v !== 0))
      } else {
        if (!Array.isArray(returnsData.data)) {
          this.setNoData()
          return
        }

        // "Live" and "Matured" modes use data array with x/y format
        categories = returnsData.data.map(item => item.x)
        const values = returnsData.data.map(item => item.y)
        this.noData = !values.some(v => v !== 0)
        const color = this.selectedMode === 'live' ? '#28c76f' : '#1a73e8'
        series = [{
          name: returnsData.name,
          type: 'bar',
          data: values,
          barWidth: '85%',
          itemStyle: {
            color,
          },
        }]
      }

      this.chartData = {
        legend: {
          show: this.selectedMode === 'all',
        },
        grid: {
          left: 70,
          right: 30,
          bottom: 70,
          top: this.selectedMode === 'all' ? 40 : 20,
          containLabel: false,
        },
        xAxis: {
          type: 'category',
          data: categories,
          name: 'Return (p.a.)',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          name: 'Frequency',
          nameLocation: 'middle',
          nameGap: 45,
          min: 0,
          max: 100,
          nameTextStyle: {
            fontSize: 13,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 12,
          },
          splitLine: {
            lineStyle: {
              opacity: this.splitLineOpacity,
              type: 'dashed',
            },
          },
        },
        series,
      }
      this.chartKey += 1
    },
  },
}
</script>

<style lang="scss" scoped>
.no-data-overlay {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
  font-size: 1.1rem;
}
</style>
